import React, { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import FieldsList from "./FieldsList";
import IgnoredTray from "./IgnoredTray";
import { IgnoreButton, ignoreItem } from "./IgnoreButton";

export default function NameDupesSection({ onChange }) {
  const [groups, setGroups] = useState(null);
  const [selected, setSelected] = useState(null);
  const [primaryId, setPrimaryId] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ignoreVersion, setIgnoreVersion] = useState(0);

  async function load() {
    try {
      const { data } = await api.get(`/cleanup/name-dupe-groups`);
      setGroups(data.groups || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load duplicates");
    }
  }
  useEffect(() => { load(); }, []);

  function openGroup(g) {
    setSelected(g);
    setPrimaryId(g.suggested_primary_id || g.records[0]?.id || "");
    setPreview(null);
  }

  async function computePreview() {
    if (!selected || !primaryId) return;
    const sec = selected.records.filter((r) => r.id !== primaryId).map((r) => r.id);
    if (sec.length === 0) { toast.error("Pick a different primary; no secondaries left"); return; }
    try {
      const { data } = await api.post(`/cleanup/phone-groups/preview`, {
        primary_id: primaryId, secondary_ids: sec,
      });
      setPreview(data.preview);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    }
  }

  async function applyMerge() {
    if (!selected || !primaryId) return;
    const sec = selected.records.filter((r) => r.id !== primaryId).map((r) => r.id);
    setBusy(true);
    try {
      const { data } = await api.post(`/cleanup/name-dedupe`, {
        primary_id: primaryId, secondary_ids: sec,
      });
      toast.success(
        `Merged ${data.soft_deleted} duplicate${data.soft_deleted === 1 ? "" : "s"} into the kept record`,
        { description: `${data.open_houses_repointed} open-house listing(s) re-pointed · duplicate moved to Recycle Bin (restorable)` },
      );
      setSelected(null); setPreview(null);
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    } finally { setBusy(false); }
  }

  // Reject-and-delete a single record from the same-name dedupe tray without
  // merging. Used to trash an open-house-created dupe while leaving the
  // completed realtor profile untouched.
  async function rejectRecord(record) {
    if (!record?.id) return;
    setBusy(true);
    try {
      await api.delete(`/cleanup/realtor/${record.id}`);
      toast.success(`Deleted ${record.name || "record"}`, {
        description: "Moved to Recycle Bin. Group updated.",
      });
      setSelected((cur) =>
        cur
          ? { ...cur, records: cur.records.filter((r) => r.id !== record.id) }
          : cur,
      );
      if (primaryId === record.id) setPrimaryId("");
      setPreview(null);
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  }

  async function afterRestore() {
    await load();
    setIgnoreVersion((v) => v + 1);
    onChange?.();
  }

  async function ignoreGroup(g) {
    setBusy(true);
    try {
      await ignoreItem(
        { kind: "name_group", key: g.key, member_ids: g.records.map((r) => r.id) },
        { label: g.key, onUndo: afterRestore },
      );
      setGroups((gs) => (gs || []).filter((x) => x.key !== g.key));
      setIgnoreVersion((v) => v + 1);
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ignore failed");
    } finally { setBusy(false); }
  }

  if (groups === null) return <div className="text-sm text-slate-500">Loading…</div>;
  if (groups.length === 0) {
    return (
      <div className="space-y-3">
        <div className="text-sm text-slate-500" data-testid="name-dupes-empty">
          No same-name duplicates found.
        </div>
        <IgnoredTray kind="name_group" version={ignoreVersion} onRestored={afterRestore} />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="name-dupes-section">
      <div className="text-xs text-slate-500">
        These records share the same person name after stripping nicknames and corporate
        suffixes (mostly created by open-house imports before the matcher was fixed).
        Click a group, confirm which record to keep, and merge — missing contact info and
        socials are copied into the kept record, its open-house listings are re-pointed,
        and the duplicate goes to the Recycle Bin (restorable).
      </div>
      <div className="grid gap-2 max-h-[500px] overflow-y-auto">
        {groups.map((g) => {
          const slug = g.key.replace(/\s+/g, "-");
          return (
            <div
              key={g.key}
              className="rounded-md border border-slate-200 bg-white p-3 flex items-center gap-2 min-h-[60px]"
              data-testid={`name-dupe-group-row-${slug}`}
            >
              <button
                type="button"
                onClick={() => openGroup(g)}
                className="flex-1 min-w-0 flex items-center justify-between gap-3 text-left hover:bg-slate-50/60 -m-1 p-1 rounded transition"
                data-testid={`name-dupe-group-${slug}`}
              >
                <div className="min-w-0">
                  <div className="font-medium text-sm text-slate-900 capitalize">{g.key}</div>
                  <div className="text-xs text-slate-500 mt-0.5 truncate">
                    {g.records.map((r) => `${r.name} (${r.source})`).join(" · ")}
                  </div>
                </div>
                <Badge variant="secondary">{g.records.length}</Badge>
              </button>
              <IgnoreButton
                onClick={() => ignoreGroup(g)}
                disabled={busy}
                testId={`name-dupe-ignore-${slug}`}
              />
            </div>
          );
        })}
      </div>

      <IgnoredTray kind="name_group" version={ignoreVersion} onRestored={afterRestore} />

      <Dialog open={!!selected} onOpenChange={() => { setSelected(null); setPreview(null); }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="name-dupe-merge-dialog">
          <DialogHeader>
            <DialogTitle>Merge duplicate realtor</DialogTitle>
            <DialogDescription>
              Pick the record to keep. The other record&apos;s missing fields are copied in,
              its open-house listings are re-pointed, and it moves to the Recycle Bin.
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
                {selected.records.map((r) => {
                  const isPrimary = r.id === primaryId;
                  return (
                    <div
                      key={r.id}
                      className={`relative text-left p-3 rounded-md border transition ${
                        isPrimary ? "border-emerald-400 bg-emerald-50 ring-2 ring-emerald-200" : "border-slate-200 bg-white hover:bg-slate-50"
                      }`}
                      data-testid={`name-dupe-candidate-${r.id}`}
                    >
                      <button
                        type="button"
                        onClick={() => { setPrimaryId(r.id); setPreview(null); }}
                        className="absolute inset-0 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-400"
                        aria-label={isPrimary ? "Selected as keeper" : `Keep ${r.name}`}
                        data-testid={`name-dupe-keep-${r.id}`}
                      />
                      <div className="relative flex items-center justify-between gap-2 pointer-events-none">
                        <span className="font-medium text-slate-900 truncate">{r.name}</span>
                        <div className="flex items-center gap-1 pointer-events-auto">
                          {isPrimary && <Badge className="bg-emerald-600">KEEP</Badge>}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (
                                window.confirm(
                                  `Delete "${r.name}" and remove it from the merge tray? It will move to the Recycle Bin. Other records here are untouched.`,
                                )
                              ) {
                                rejectRecord(r);
                              }
                            }}
                            className="h-7 w-7 inline-flex items-center justify-center rounded-full border border-rose-200 bg-white text-rose-600 hover:bg-rose-50 hover:text-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-300"
                            title="Reject &amp; delete this row (won't touch the keeper)"
                            data-testid={`name-dupe-reject-${r.id}`}
                            disabled={busy}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                      <div className="relative pointer-events-none">
                        <FieldsList r={r} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
                <Button variant="outline" onClick={computePreview} data-testid="name-dupe-preview-btn">
                  Preview merge
                </Button>
              </div>
              {preview && (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
                  <div className="text-xs font-medium text-emerald-700 mb-2">MERGE PREVIEW</div>
                  <FieldsList r={preview} />
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => { setSelected(null); setPreview(null); }}>Cancel</Button>
                <Button onClick={applyMerge} disabled={busy || !primaryId} data-testid="name-dupe-merge-apply">
                  {busy ? "Merging…" : "Merge duplicates"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
